import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Edit, Trash2, BookOpen, Users, ChevronRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import BranchLabel from './ui/BranchLabel';
import useBranchLabels from '../hooks/useBranchLabels';
import ClassFormDialog, { classUsesSections } from './classes/ClassFormDialog';

const normSubjectId = (id) => {
  const n = Number(id);
  return Number.isNaN(n) || n <= 0 ? null : n;
};

const subjectIdsFromClass = (cls) => (cls?.subjects || [])
  .map((s) => normSubjectId(s.id ?? s.subject_id))
  .filter((id) => id != null);

const Classes = () => {
  const { user, canManageFeature } = useAuth();
  const canManageClasses = canManageFeature('classes');
  const canManageSubjects = canManageFeature('subjects');
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState('full');
  const [editingClass, setEditingClass] = useState(null);
  const { toast } = useToast();
  const { showBranchLabels } = useBranchLabels();
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);

  useEffect(() => { fetchClasses(); }, [user?.school_id]);
  useEffect(() => { fetchSubjects(); }, [user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => fetchClasses();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [user?.school_id]);

  const fetchClasses = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = academicYearId ? { academic_year_id: academicYearId } : {};
      const response = await api.get('/classes', { params });
      setClasses(response.data.data || []);
    } catch (error) {
      const status = error.response?.status;
      const apiMsg = error.response?.data?.message;
      const detail = apiMsg
        || (status ? `Server returned ${status}` : error.message || 'Network error');
      toast({
        title: 'Error',
        description: `Failed to fetch classes${detail ? `: ${detail}` : ''}`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await api.get('/subjects');
      setSubjects(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch subjects');
    }
  };

  const buildSubjectIdsPayload = () => selectedSubjectIds
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id) && id > 0);

  const handleSubjectsSubmit = async (e) => {
    e.preventDefault();
    if (!editingClass) return;
    const subjectIds = buildSubjectIdsPayload();
    try {
      await api.put(`/classes/${editingClass.id}/subjects`, { subject_ids: subjectIds });
      toast({ title: 'Success', description: 'Assigned subjects updated' });
      setIsDialogOpen(false);
      resetSubjectsForm();
      fetchClasses();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed', variant: 'destructive' });
    }
  };

  const handleEdit = async (cls, mode = 'full') => {
    let detail = cls;
    try {
      const res = await api.get(`/classes/${cls.id}`);
      detail = res.data?.data ?? cls;
    } catch {
      detail = cls;
    }

    if (!detail.subjects?.length) {
      try {
        const subRes = await api.get(`/classes/${cls.id}/subjects`);
        const assigned = subRes.data?.data ?? [];
        if (assigned.length) {
          detail = { ...detail, subjects: assigned };
        }
      } catch {
        // keep detail from class show or list
      }
    }

    setEditMode(mode);
    setEditingClass(detail);
    if (mode === 'subjects-only') {
      setSelectedSubjectIds(subjectIdsFromClass(detail));
    }
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this class?')) {
      try {
        await api.delete(`/classes/${id}`);
        toast({ title: 'Success', description: 'Class deleted' });
        fetchClasses();
      } catch (error) {
        toast({ title: 'Error', description: 'Failed', variant: 'destructive' });
      }
    }
  };

  const resetSubjectsForm = () => {
    setEditingClass(null);
    setEditMode('full');
    setSelectedSubjectIds([]);
  };

  const openAddClass = () => {
    setEditMode('full');
    setEditingClass(null);
    setIsDialogOpen(true);
  };

  const handleClassFormSuccess = () => {
    setIsDialogOpen(false);
    setEditingClass(null);
    fetchClasses();
  };

  return (
    <div className="space-y-6" data-testid="classes-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Classes</h1>
          <p className="text-muted-foreground">Manage classes and sections</p>
        </div>
        {canManageClasses && (
          <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-class-btn" onClick={openAddClass}>
            <Plus className="w-4 h-4 mr-2" /> Add Class
          </Button>
        )}
        {canManageClasses && (
          <ClassFormDialog
            open={isDialogOpen && editMode === 'full'}
            onOpenChange={(open) => {
              if (editMode !== 'full') return;
              setIsDialogOpen(open);
              if (!open) setEditingClass(null);
            }}
            editingClass={editMode === 'full' ? editingClass : null}
            academicYearId={getSelectedAcademicYearId()}
            onSuccess={handleClassFormSuccess}
          />
        )}
        {(canManageSubjects && !canManageClasses) && (
        <Dialog
          open={isDialogOpen && editMode === 'subjects-only'}
          onOpenChange={(open) => {
            if (editMode !== 'subjects-only') return;
            setIsDialogOpen(open);
            if (!open) resetSubjectsForm();
          }}
        >
          <DialogContent className="border-border text-foreground max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Assigned Subjects
                {editingClass ? ` — ${editingClass.name}` : ''}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubjectsSubmit} className="space-y-4">
              <div>
                <Label>Assigned Subjects</Label>
                {subjects.length === 0 ? (
                  <p className="text-xs text-muted-foreground mt-2">No subjects found. Add subjects first.</p>
                ) : (
                  <div className="mt-2 grid grid-cols-2 gap-2 max-h-60 overflow-y-auto rounded-md border border-border p-2">
                    {subjects.map((subject) => (
                      <label key={subject.id} className="flex items-center gap-2 text-sm text-foreground">
                        <input
                          type="checkbox"
                          className="accent-blue-500"
                          checked={selectedSubjectIds.some((sid) => normSubjectId(sid) === normSubjectId(subject.id))}
                          onChange={(e) => {
                            const subjectId = normSubjectId(subject.id);
                            if (!subjectId) return;
                            setSelectedSubjectIds((prev) => {
                              if (e.target.checked) {
                                if (prev.some((sid) => normSubjectId(sid) === subjectId)) return prev;
                                return [...prev, subjectId];
                              }
                              return prev.filter((sid) => normSubjectId(sid) !== subjectId);
                            });
                          }}
                        />
                        <span>{subject.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Save Subjects</Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>
      ) : classes.length === 0 ? (
        <Card className="border-border"><CardContent className="py-12 text-center">
          <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No classes found</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes.map((cls) => (
            <Card key={cls.id} className="border-border hover:border-border transition-colors">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Link to={`/classes/${cls.id}`} className="flex items-center gap-2 min-w-0 flex-1">
                    <CardTitle className="text-foreground flex items-center gap-2 hover:text-blue-400 transition-colors">
                      <BookOpen className="w-5 h-5 text-blue-400 shrink-0" />
                      <span className="truncate">{cls.name}</span>
                      <BranchLabel name={cls.branch_name} show={showBranchLabels} />
                      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    </CardTitle>
                  </Link>
                  {(canManageClasses || canManageSubjects) && (
                  <div className="flex gap-1 shrink-0">
                    {canManageClasses && (
                      <>
                        <Button type="button" variant="ghost" size="icon" onClick={() => handleEdit(cls, 'full')} className="text-muted-foreground hover:text-foreground h-8 w-8">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" onClick={() => handleDelete(cls.id)} className="text-muted-foreground hover:text-red-400 h-8 w-8">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {canManageSubjects && !canManageClasses && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-border text-xs h-8"
                        onClick={() => handleEdit(cls, 'subjects-only')}
                      >
                        Manage Subjects
                      </Button>
                    )}
                  </div>
                  )}
                </div>
              </CardHeader>
              <Link to={`/classes/${cls.id}`}>
                <CardContent className="cursor-pointer">
                  {cls.description && <p className="text-sm text-muted-foreground mb-3">{cls.description}</p>}
                  <div className="flex flex-wrap gap-2">
                    {classUsesSections(cls)
                      ? (cls.sections || []).map((section) => (
                        <span key={section.id} className="px-3 py-1 bg-muted text-foreground rounded-full text-sm inline-flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          <span>Section {section.name}</span>
                          {section.capacity && <span className="text-muted-foreground">({section.capacity})</span>}
                          {(section.class_teacher_first_name || section.class_teacher_last_name) && (
                            <span className="text-muted-foreground">
                              — {`${section.class_teacher_first_name || ''} ${section.class_teacher_last_name || ''}`.trim()}
                            </span>
                          )}
                        </span>
                      ))
                      : (
                        <span className="text-xs text-muted-foreground">No sections — class-level group</span>
                      )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    {classUsesSections(cls)
                      ? 'Edit class → sections & student placement. Open class → Sections for class teachers.'
                      : 'Edit class to turn on sections if needed. Open class → Class teachers for assignments.'}
                  </p>
                </CardContent>
              </Link>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Classes;
